import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { UploadCloud } from "lucide-react"
import { toast } from "sonner"
import { PageHeader, Section, Stat, StatStrip } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { QueryError, useScannerGate } from "@/features/card-scanner/shared"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"

export const Route = createFileRoute("/card-scanner/manifest")({
	component: ManifestPage,
})

function ManifestPage() {
	const allowed = useScannerGate()
	const { orpc, client, isAuthReady } = useMarketplaceOrpc()
	const queryClient = useQueryClient()

	const { data, isLoading, isError, error } = useQuery({
		...orpc.cardScanner.getManifestPreview.queryOptions(),
		enabled: allowed && isAuthReady,
	})

	const republish = useMutation({
		mutationFn: () => client.cardScanner.republishManifest(),
		onSuccess: ({ games }) => {
			toast.success(`manifest-v2.json published (${games} games)`)
			queryClient.invalidateQueries({
				queryKey: orpc.cardScanner.getManifestPreview.key(),
			})
		},
		onError: (error) => toast.error(error.message),
	})

	if (!allowed) return null

	const manifest = data?.manifest as
		| {
				generatedAt?: string
				labels?: unknown[]
				games?: unknown[]
				models?: Record<string, unknown[]>
		  }
		| undefined
	const liveModelCount = manifest?.models
		? Object.values(manifest.models).flat().length
		: 0

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Manifest v2"
				description="Live preview of manifest-v2.json; the backend is its only writer and v1 stays untouched"
				actions={
					<Button
						onClick={() => republish.mutate()}
						disabled={republish.isPending}
					>
						<UploadCloud className="h-4 w-4" />
						Republish
					</Button>
				}
			/>

			{manifest && (
				<StatStrip>
					<Stat
						label="Games"
						value={manifest.games?.length ?? 0}
						muted={!manifest.games?.length}
					/>
					<Stat
						label="Live models"
						value={liveModelCount}
						muted={liveModelCount === 0}
					/>
					<Stat
						label="Labels"
						value={manifest.labels?.length ?? 0}
						muted={!manifest.labels?.length}
					/>
					<Stat
						label="Composed"
						value={
							manifest.generatedAt
								? new Date(manifest.generatedAt).toLocaleTimeString()
								: "—"
						}
						muted={!manifest.generatedAt}
					/>
				</StatStrip>
			)}

			{isLoading ? (
				<Skeleton className="h-96 w-full" />
			) : isError ? (
				<QueryError what="the manifest preview" error={error} />
			) : (
				<Section label="manifest-v2.json">
					<pre className="max-h-[70vh] overflow-auto rounded-lg bg-muted p-4 font-mono text-xs">
						{JSON.stringify(data?.manifest, null, 2)}
					</pre>
				</Section>
			)}
		</div>
	)
}
